import { useState } from 'react';
import PropTypes from 'prop-types';
import str from 'underscore.string';
import moment from 'moment';
import { Modal, ModalBody, LinkButton, IconButton, ButtonTooltip } from '@evertrue/et-components';
import EnrichmentModalHeader from './enrichment-modal-header';
import { mostRecentCreate } from './enrichment-utils';
import Api from 'entities/helpers/api';
import EverTrue from 'app';
import EnrichmentConfidencePill from './enrichment-confidence-pill';

const gql = String.raw;

/**
 * Formats a location object for display in the modal
 */
const formatLocation = (location) => {
  const parts = [];
  if (location.city) parts.push(location.city);
  if (location.state) parts.push(location.state);
  if (parts.length > 0) return parts.join(', ');

  if (location.metro) return location.metro;
  if (location.country) return location.country;

  return 'Unknown Location';
};

/**
 * Formats an event for display in the modal
 */
const formatEvent = (event) => {
  const type = str.capitalize(event.type);
  const displayDate = event.recorded_at || event.updated_at;
  const date = moment(displayDate).format('MMM D, YYYY');

  return (
    <div className="other-details-modal--event-item">
      <div className="other-details-modal--event-text">
        <strong>{type}</strong> | Reported on {date}
      </div>
      <div className="other-details-modal--confidence-row">
        <span className="other-details-modal--confidence-label">Confidence Score:</span>
        <EnrichmentConfidencePill score={event.confidence_score} />
      </div>
    </div>
  );
};

/**
 * Handles GraphQL mutation for enrichment attributes (names)
 */
const handleNameMutation = (contactId, itemId, updatedAt, rejecting, onSuccess, onError) => {
  const params = { enrichment_attributes: [{ id: itemId, rejected: rejecting, updated_at: updatedAt }] };

  const query = gql`
    mutation ($id: BigInt!, $enrichment: EnrichmentInput) {
      contact_mutation(id: $id, enrichment: $enrichment) {
        code
        enrichment_mutation {
          enrichment_attributes_mutations {
            success
          }
        }
      }
    }
  `;

  Api.GRAPHQL.GRAPHQL.post({
    data: JSON.stringify({
      query: String.raw`${query}`,
      variables: { id: contactId, enrichment: params },
    }),
  })
    .then(() => onSuccess(rejecting ? 'Name Removed' : 'Name Restored'))
    .catch(() => onError(rejecting ? 'removing name' : 'restoring name'));
};

/**
 * Handles GraphQL mutation for contact locations
 */
const handleLocationMutation = (contactId, itemId, updatedAt, rejecting, onSuccess, onError) => {
  const params = { contact_locations: [{ id: itemId, rejected: rejecting, updated_at: updatedAt }] };

  const query = gql`
    mutation ($id: BigInt!, $enrichment: EnrichmentInput) {
      contact_mutation(id: $id, enrichment: $enrichment) {
        code
        enrichment_mutation {
          contact_locations_mutations {
            success
          }
        }
      }
    }
  `;

  Api.GRAPHQL.GRAPHQL.post({
    data: JSON.stringify({
      query: String.raw`${query}`,
      variables: { id: contactId, enrichment: params },
    }),
  })
    .then(() => onSuccess(rejecting ? 'Location Removed' : 'Location Restored'))
    .catch(() => onError(rejecting ? 'removing location' : 'restoring location'));
};

/**
 * Handles Graph QL mutation for wealth life events
 */
const handleWealthEventMutation = (contactId, itemId, updatedAt, rejecting, onSuccess, onError) => {
  const params = { wealth_life_events: [{ id: itemId, rejected: rejecting, updated_at: updatedAt }] };

  const query = gql`
    mutation ($id: BigInt!, $enrichment: EnrichmentInput) {
      contact_mutation(id: $id, enrichment: $enrichment) {
        code
        enrichment_mutation {
          wealth_life_events_mutations {
            success
          }
        }
      }
    }
  `;

  Api.GRAPHQL.GRAPHQL.post({
    data: JSON.stringify({
      query: String.raw`${query}`,
      variables: { id: contactId, enrichment: params },
    }),
  })
    .then(() => onSuccess(rejecting ? 'Event Removed' : 'Event Restored'))
    .catch(() => onError(rejecting ? 'removing event' : 'restoring event'));
};

/**
 * Handles GraphQL mutation for employment events
 */
const handleEmploymentEventMutation = (contactId, itemId, updatedAt, rejecting, onSuccess, onError) => {
  const params = { employment_events: [{ id: itemId, rejected: rejecting, updated_at: updatedAt }] };

  const query = gql`
    mutation ($id: BigInt!, $enrichment: EnrichmentInput) {
      contact_mutation(id: $id, enrichment: $enrichment) {
        code
        enrichment_mutation {
          employment_events_mutations {
            success
          }
        }
      }
    }
  `;

  Api.GRAPHQL.GRAPHQL.post({
    data: JSON.stringify({
      query: String.raw`${query}`,
      variables: { id: contactId, enrichment: params },
    }),
  })
    .then(() => onSuccess(rejecting ? 'Event Removed' : 'Event Restored'))
    .catch(() => onError(rejecting ? 'removing event' : 'restoring event'));
};

const OtherDetailsModal = ({ isOpen, closeModal, contact, enrichmentData, onDelete }) => {
  const {
    wealth_life_events: wealthLifeEvents = [],
    employment_events: employmentEvents = [],
    enrichment_attributes: enrichmentAttributes = [],
    contact_locations: contactLocations = [],
  } = enrichmentData;

  const [showRejected, setShowRejected] = useState(false);

  // Separate active and rejected items
  // Note: Events don't have rejected field in API, so treat all as active
  const allActiveEvents = [...(wealthLifeEvents || []), ...(employmentEvents || [])];
  const activeNames = enrichmentAttributes.filter((name) => !name.rejected);
  const rejectedNames = enrichmentAttributes.filter((name) => name.rejected);
  const activeLocations = contactLocations.filter((location) => !location.rejected);
  const rejectedLocations = contactLocations.filter((location) => location.rejected);

  // Calculate last update date
  const allData = [...wealthLifeEvents, ...employmentEvents, ...enrichmentAttributes, ...contactLocations];
  const lastUpdateDate = allData.length > 0 ? mostRecentCreate(allData, 'updated_at', 'MMM DD, YYYY') : null;

  // Mutation success/error handlers
  const onSuccess = (message) => {
    EverTrue.Alert.success(message);
    onDelete(); // Trigger refresh - IMPORTANT: modal stays open
  };

  const onError = (action) => {
    EverTrue.Alert.error(`Problem ${action} record. Please try again later`);
  };

  // Handle mutations based on item type
  const handleMutation = (item, type, rejecting) => {
    const contactId = contact.id;

    switch (type) {
      case 'name':
        handleNameMutation(contactId, item.id, item.updated_at, rejecting, onSuccess, onError);
        break;
      case 'location':
        handleLocationMutation(contactId, item.id, item.updated_at, rejecting, onSuccess, onError);
        break;
      case 'wealth_event':
        handleWealthEventMutation(contactId, item.id, item.updated_at, rejecting, onSuccess, onError);
        break;
      case 'employment_event':
        handleEmploymentEventMutation(contactId, item.id, item.updated_at, rejecting, onSuccess, onError);
        break;
      default:
        console.error('Unknown mutation type:', type);
    }
  };

  const renderListItem = (item, type, labelFormatter, deleteLabel, restoreLabel, canDelete = true) => (
    <div className="profile-enrichment--list contact-interests-enrichment" key={`${type}-${item.id}`}>
      <div className="profile-enrichment-item">
        <div className="other-details-modal--item-content">
          <p>{labelFormatter(item)}</p>
          <div className="other-details-modal--confidence-row">
            <span className="other-details-modal--confidence-label">Confidence Score:</span>
            <EnrichmentConfidencePill score={item.confidence_score} />
          </div>
        </div>
        {canDelete &&
          (item.rejected ? (
            <ButtonTooltip
              children={restoreLabel}
              contentPadding={'4px 8px'}
              trigger={
                <LinkButton title="" onClick={() => handleMutation(item, type, false)}>
                  Undo
                </LinkButton>
              }
            />
          ) : (
            <ButtonTooltip
              children={deleteLabel}
              contentPadding={'4px 8px'}
              trigger={
                <IconButton
                  title=""
                  icon="clear"
                  className={'profile--enrichment-delete-button'}
                  onClick={() => handleMutation(item, type, true)}
                />
              }
            />
          ))}
      </div>
    </div>
  );

  const hasRejectedItems = rejectedNames.length > 0 || rejectedLocations.length > 0;

  return (
    <Modal isOpen={isOpen} closeModal={closeModal} size="500px" closeOnOutsideClick={true}>
      <EnrichmentModalHeader
        headerText={`${contact.name}'s Other Details`}
        closeModal={closeModal}
        updateDate={lastUpdateDate}
      />
      <ModalBody>
        <div className="profile-enrichment--more-modal">
          {/* NOTEWORTHY EVENTS Section */}
          {allActiveEvents.length > 0 && (
            <div className="profile-enrichment--lists">
              <div className="profile-enrichment--section-title">NOTEWORTHY EVENTS</div>
              {allActiveEvents.map((event) => (
                <div className="profile-enrichment--list" key={`event-${event.id}`}>
                  {formatEvent(event)}
                </div>
              ))}
            </div>
          )}

          {/* Names Section */}
          {activeNames.length > 0 && (
            <div className="profile-enrichment--lists">
              <div className="profile-enrichment--section-title">Names</div>
              {activeNames.map((name) => renderListItem(name, 'name', (n) => n.value, 'Delete name', 'Restore name'))}
            </div>
          )}

          {/* Locations Section */}
          {activeLocations.length > 0 && (
            <div className="profile-enrichment--lists">
              <div className="profile-enrichment--section-title">Locations</div>
              {activeLocations.map((location) =>
                renderListItem(location, 'location', formatLocation, 'Delete location', 'Restore location')
              )}
            </div>
          )}

          {/* See Deleted Toggle */}
          {hasRejectedItems && (
            <LinkButton onClick={() => setShowRejected(!showRejected)} title="">
              {showRejected ? 'Hide deleted' : 'See deleted'}
            </LinkButton>
          )}

          {/* Deleted Items Section */}
          {showRejected && (
            <div style={{ marginTop: '15px' }}>
              {rejectedNames.length > 0 && (
                <div className="profile-enrichment--lists">
                  <div className="profile-enrichment--section-title">Names</div>
                  {rejectedNames.map((name) =>
                    renderListItem(name, 'name', (n) => n.value, 'Delete name', 'Restore name')
                  )}
                </div>
              )}

              {rejectedLocations.length > 0 && (
                <div className="profile-enrichment--lists">
                  <div className="profile-enrichment--section-title">Locations</div>
                  {rejectedLocations.map((location) =>
                    renderListItem(location, 'location', formatLocation, 'Delete location', 'Restore location')
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

OtherDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  contact: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  enrichmentData: PropTypes.shape({
    wealth_life_events: PropTypes.array,
    employment_events: PropTypes.array,
    enrichment_attributes: PropTypes.array,
    contact_locations: PropTypes.array,
  }).isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default OtherDetailsModal;
